/**
 * EL ARMADOR UNA PANTALLA A LA VEZ (MAD-ASISTENTE-UN-PASO-T1, maqueta aprobada por Josse, Ares #43601).
 *
 * AUDIENCIA: el asesor de Maderinco armando el presupuesto con el cliente, en el computador (1366) o el celular (375),
 * solo el negocio demo. Contrato de pantalla: la maqueta https://claude.ai/artifact/FTvBEHapj9gvLDEV5c6pc7 (version
 * 1793763895-2c02), vista "Armar presupuesto".
 *
 * Arriba el nombre de la seccion, "Seccion n de 10", la barra de las 10 secciones y los pasos de esta (Tipo, Cantidades,
 * Revision); en medio la pantalla del paso; abajo, fija, el total con Atras y "Siguiente: <lo que sigue>". Nada de la
 * seccion siguiente a la vista. Las pantallas las decide application/presupuestos/asistentePasos.js; quien pinta pasa
 * el cuerpo de Cantidades y Revision y escucha [data-asi-tipo], [data-asi-cantidad], [data-asi-atras],
 * [data-asi-siguiente] y [data-asi-noaplica]. Estilos .asi-* en artifacts.css.
 *
 * @module presentation/components/presupuestos/AsistentePasos
 */
import { esc } from '../../shared/texto.js';
import { formatearCOP } from '../../shared/numeroColombiano.js';

const UNIDAD = { m2: 'm²', 'm²': 'm²', ml: 'ml', m: 'm', und: 'und', un: 'und', gnral: 'global', gl: 'global', m3: 'm³' };
export const unidadBonita = (u) => UNIDAD[String(u || '').toLowerCase()] || (u ? String(u) : 'und');
// Pesos enteros con su signo, como la maqueta ('$ 231.562'): formatearCOP no pone el signo y muestra centavos.
const plata = (n) => '$ ' + formatearCOP(Math.round(Number(n) || 0));
const mayus = (t) => String(t || '').charAt(0).toUpperCase() + String(t || '').slice(1);

/** El tono del bloque de muestra: fijo por material (el nombre manda), uno de 12 definidos en artifacts.css. */
export function tonoDe(nombre) {
  let h = 0;
  for (const c of String(nombre || '')) h = (h * 31 + c.charCodeAt(0)) % 997;
  return h % 12;
}

/**
 * La pantalla Tipo: una tarjeta por tipo (bloque de color con el material y el precio por unidad) y la cantidad una
 * sola vez; cambiar el tipo recalcula el subtotal sin perder la cantidad.
 * @param {{linea: string, tarjetas: Array, elegido?: string, cantidad?: string, subtotal?: number|null}} t
 */
export function pantallaTipoHTML({ linea, tarjetas, elegido, cantidad, subtotal }) {
  const sel = tarjetas.find((x) => x.tipo === elegido);
  const unidad = unidadBonita(sel?.unidad || tarjetas[0]?.unidad);
  return `<p class="asi-pregunta">&iquest;Qu&eacute; ${esc(String(linea).toLowerCase())} lleva?</p>
    <p class="asi-ayuda">Elige uno. Cambiarlo despu&eacute;s recalcula el valor sin perder la cantidad.</p>
    <div class="asi-tipos" role="radiogroup" aria-label="Tipo de ${esc(linea)}">${tarjetas.map((x) => `
      <button type="button" class="asi-tipo" role="radio" aria-checked="${x.tipo === elegido}" data-asi-tipo="${esc(x.tipo)}">
        <span class="asi-muestra" data-tono="${tonoDe(x.tipo)}">${esc(mayus(x.tipo))}</span>
        <span class="asi-dato"><b>${x.precio === null ? 'sin precio' : plata(x.precio)} / ${unidadBonita(x.unidad)}</b>${x.tipo === elegido ? '<span class="asi-elegido">Elegido</span>' : x.porConfirmar ? '<span>precio de referencia</span>' : ''}</span>
      </button>`).join('')}</div>
    <div class="asi-area">
      <label for="asi-cantidad">Cantidad de ${esc(String(linea).toLowerCase())}
        <span class="asi-fila"><input id="asi-cantidad" class="form-input asi-campo" inputmode="decimal" data-asi-cantidad value="${esc(cantidad || '')}" placeholder="ej. 104,66"><span class="asi-unidad">${esc(unidad)}</span></span></label>
      <div class="asi-subtotal" role="status"><small>${sel ? esc(mayus(sel.tipo)) : 'Sin tipo elegido'}</small><b>${subtotal ? plata(subtotal) : sel ? 'Escribe la cantidad' : '—'}</b></div>
    </div>`;
}

/**
 * Todo el asistente: encabezado, pantalla y barra fija.
 * @param {{pantallas: Array, idx: number, secciones: Array<{numero, nombre, corto}>, noAplica: Set<number>,
 *   conPartidas: Set<number>, cuerpoHTML: string, total: number, siguienteTexto: string}} o
 */
export function asistenteHTML({ pantallas, idx, secciones, noAplica, conPartidas, cuerpoHTML, total, siguienteTexto }) {
  const p = pantallas[idx];
  const resumen = p.paso === 'resumen';
  const sec = secciones.find((s) => s.numero === p.n);
  const avance = secciones.map((s) => {
    const clase = s.numero === p.n ? 'actual' : noAplica.has(s.numero) ? 'no-aplica' : (resumen || conPartidas.has(s.numero) || pantallas.findIndex((x) => x.n === s.numero) < idx) ? 'hecha' : '';
    return `<i class="${clase}" title="${esc(s.nombre)}${noAplica.has(s.numero) ? ' (no aplica)' : ''}"></i>`;
  }).join('');
  const propias = resumen ? [] : pantallas.filter((x) => x.n === p.n);
  const subpasos = propias.map((x) => `<span class="${x === p ? 'actual' : pantallas.indexOf(x) < idx ? 'hecho' : ''}">${esc(x.titulo)}</span>`).join('');
  return `<div class="asi" data-asistente data-asi-paso="${esc(p.paso)}" data-asi-seccion="${p.n ?? ''}">
    <header class="asi-cabeza">
      <div class="asi-cabeza-fila"><h2 class="asi-seccion">${resumen ? 'Resumen' : esc(sec?.nombre || '')}</h2>${resumen ? '' : `<span class="asi-de">Secci&oacute;n ${p.n} de ${secciones.length}</span>`}</div>
      <div class="asi-avance" aria-hidden="true">${avance}</div>
      ${subpasos ? `<div class="asi-subpasos">${subpasos}</div>` : ''}
    </header>
    <div class="asi-cuerpo">${cuerpoHTML}</div>
    ${resumen ? '' : `<button type="button" class="asi-noaplica" data-asi-noaplica>${noAplica.has(p.n) ? 'Esta secci&oacute;n s&iacute; aplica' : 'Esta secci&oacute;n no aplica en esta obra'}</button>`}
    <div class="asi-barra" role="region" aria-label="Total y pasos">
      <div class="asi-barra-dentro">
        <div class="asi-total"><small>Total con utilidad e IVA</small><b data-asi-total>${plata(total)}</b></div>
        <button type="button" class="asi-atras" data-asi-atras${idx === 0 ? ' disabled' : ''}>Atr&aacute;s</button>
        <button type="button" class="asi-siguiente" data-asi-siguiente>${esc(siguienteTexto)}</button>
      </div>
    </div>
  </div>`;
}

function filaCantidadHTML(f) {
  const o = f.opciones[f.op];
  const u = unidadBonita(o?.unidad);
  const marca = f.obligatoria
    ? `<button type="button" class="asi-marca" disabled aria-label="${esc(f.linea)} es obligatoria">Va</button>`
    : `<button type="button" class="asi-marca" role="checkbox" aria-checked="${f.incluida}" data-asi-marca="${f.i}" aria-label="Incluir ${esc(f.linea)}">${f.incluida ? '&#10003;' : ''}</button>`;
  // MAD-CUAL-CORTO-T1: el Cual son tarjetas con lo que distingue a cada opcion (etiquetasCortas) y su precio.
  const varias = f.opciones.length > 1;
  const cortas = f.cortas || [];
  const cual = varias
    ? `<div class="asi-cual"><div class="asi-cual-titulo">Cu&aacute;l${(f.distingue || []).length ? ` &middot; <b>${esc(f.distingue.join(' y ').toLowerCase())}</b>` : ''}</div>
      <div class="asi-ops" role="radiogroup" aria-label="Cu&aacute;l ${esc(f.linea)}">${f.opciones.map((x, k) => `<button type="button" class="asi-opc" role="radio" aria-checked="${k === f.op}" data-asi-op="${f.i}" data-k="${k}">
        <b>${esc(cortas[k]?.corto || x.etiqueta)}</b><span>${plata(x.precio)} / ${esc(unidadBonita(x.unidad))}</span>${cortas[k]?.extra ? `<small>${esc(cortas[k].extra)}</small>` : ''}</button>`).join('')}</div></div>`
    : '';
  return `<div class="asi-fila-c${f.incluida ? '' : ' fuera'}" data-asi-fila="${f.i}">
    ${marca}
    <div class="asi-fila-nombre"><div class="asi-fila-titulo"><b>${esc(f.linea)}${f.obligatoria ? '<span class="asi-oblig">obligatoria</span>' : ''}</b>${f.editable
      ? `<button type="button" class="asi-lapiz" data-asi-lapiz="${f.i}" aria-expanded="${!!f.editando}" aria-label="Editar el &iacute;tem de ${esc(f.linea)}" title="Editar el &iacute;tem">&#9998;</button>` : ''}</div>${varias ? '' : `<span>${o ? `${plata(o.precio)} / ${esc(u)}` : 'sin precio en la plantilla'}</span>`}</div>
    <div class="asi-fila-valor">${plata(f.valor)}</div>
    ${f.incluida ? `<div class="asi-fila-control">
      <input class="form-input asi-campo" id="asi-cant-${f.i}" inputmode="decimal" data-asi-cant="${f.i}" aria-label="Cantidad de ${esc(f.linea)}" value="${esc(f.cantidadTexto || '')}"><span class="asi-unidad">${esc(u)}</span>
      ${f.cantidadTexto ? '' : '<span class="asi-falta">falta la cantidad</span>'}</div>
    ${cual}` : ''}
    ${f.editando ? `<div class="asi-editor" data-asi-editor="${f.i}"></div>` : ''}
    ${f.guardado ? '<p class="asi-guardado" role="status">Guardado. El nombre que queda es el que armó la base.</p>' : ''}
  </div>`;
}

/** Cantidades: primero lo obligatorio, despues "Si la obra lo lleva". filas = [{ i, linea, obligatoria, opciones, op, incluida, cantidadTexto, valor }]. */
export function pantallaCantidadesHTML(filas) {
  const oblig = filas.filter((f) => f.obligatoria);
  const resto = filas.filter((f) => !f.obligatoria);
  return `<p class="asi-pregunta">&iquest;Cu&aacute;nto lleva de cada cosa?</p>
    <p class="asi-ayuda">Primero lo obligatorio. Lo dem&aacute;s se marca solo si la obra lo lleva.</p>
    ${oblig.length ? `<section class="asi-grupo"><h3 class="asi-grupo-titulo">Obligatorio (${oblig.length})</h3>${oblig.map(filaCantidadHTML).join('')}</section>` : ''}
    ${resto.length ? `<section class="asi-grupo"><h3 class="asi-grupo-titulo">Si la obra lo lleva (${resto.length})</h3>${resto.map(filaCantidadHTML).join('')}</section>` : ''}
    ${filas.length ? '' : '<p class="asi-ayuda">Esta secci&oacute;n no pide cantidades: sigue a la revisi&oacute;n.</p>'}`;
}

/** Revision: cada linea de chequeo con Confirmado / Pendiente / No aplica; lo que va dentro de otra partida, dicho. */
export function pantallaRevisionHTML({ chequeo, dentro }) {
  const tres = (c) => `<div class="asi-tres" role="group" aria-label="${esc(c.linea)}">${[['confirmado', 'Confirmado'], ['pendiente', 'Pendiente'], ['no_aplica', 'No aplica']]
    .map(([k, t]) => `<button type="button" data-asi-cheq="${c.i}" data-v="${k}" aria-pressed="${c.valor === k}">${t}</button>`).join('')}</div>`;
  return `<p class="asi-pregunta">Revisa con el cliente</p>
    <p class="asi-ayuda">Esto no pide cantidad: se confirma con el cliente, queda pendiente o no aplica en esta obra.</p>
    <div class="asi-grupo">${chequeo.map((c) => `<div class="asi-cheq"><b>${esc(c.linea)}</b>${c.nota ? `<p>${esc(c.nota)}</p>` : ''}${tres(c)}</div>`).join('')}</div>
    ${dentro.length ? `<section class="asi-grupo"><h3 class="asi-grupo-titulo">Ya va dentro de otra partida (no se cobra aparte)</h3>${dentro.map((d) => `<div class="asi-dentro"><b>${esc(d.linea)}</b>${d.nota ? ' &middot; ' + esc(d.nota) : ''}</div>`).join('')}</section>` : ''}`;
}

/** Resumen: lo que suma cada seccion con lo pendiente, y subtotal, utilidad, IVA y total. */
export function pantallaResumenHTML({ secciones, aiu, utilidadPct, ivaPct }) {
  const pct = (n) => esc(String(n).replace('.', ','));
  return `<div class="asi-resumen">
    ${secciones.map((s) => `<div><span>${esc(s.nombre)}${s.noAplica ? ' <span class="asi-falta">&middot; no aplica</span>' : s.pendientes ? ` <span class="asi-falta">&middot; ${s.pendientes} por confirmar</span>` : ''}</span><span>${plata(s.valor)}</span></div>`).join('')}
    <div><span>Subtotal</span><span>${plata(aiu.subtotal)}</span></div>
    <div><span>Utilidad ${pct(utilidadPct)} %</span><span>${plata(aiu.utilidad)}</span></div>
    <div><span>IVA ${pct(ivaPct)} % sobre la utilidad</span><span>${plata(aiu.ivaUtilidad)}</span></div>
    ${aiu.adicionales && aiu.adicionales.total > 0 ? `<div><span>Adicionales con su AIU</span><span>${plata(aiu.adicionales.total)}</span></div>` : ''}
    <div class="asi-grande"><span>Total</span><span>${plata(aiu.total)}</span></div>
  </div>
  <p class="asi-ayuda">Pon el c&oacute;digo, el proyecto y el cliente con Editar, arriba, y guarda.</p>`;
}
